import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { AMMUNITION_STOCK_TYPE, AmmunitionStock, AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, COST_CENTRE_TYPE, CostCentre, setDefaultLocationFromUser } from 'assets-model';
import { AmmunitionStockService, BusinessUnitService, CostCentreService } from 'assets-service';


@Directive()
export abstract class AmmunitionStockItemComponentBase extends CrudItemComponent<AmmunitionStock, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  businessUnitSelectionList: BusinessUnit[];

  costCentreSelectionList: CostCentre[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected ammunitionStockService: AmmunitionStockService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(AMMUNITION_STOCK_TYPE, fb, elementRef, router, route, userStorage, dialog, ammunitionStockService, injector, snackbar);
  }

  createNewItem() {
    return new AmmunitionStock();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      ammunitionStockId: [null, []],
      businessUnit: [null, [requiredValidator,]],
      costCentre: [null, [requiredValidator,]],
      calibre: [null, [requiredValidator,]],
      closed: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      currentStockAmount: [null, []],
      hasStocktake: [null, []],
      lockVersion: [null, []],
      make: [null, [requiredValidator,]],
      updatedBy: [null, []],
      updatedDate: [null, []],
      weight: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.ammunitionStockId?.disable();
    }
    this.currentStockAmount?.disable();
    this.hasStocktake?.disable();
  }
  
  ammunitionStockIdChanging(value: number, unsetDependents: boolean = true) {}
  
  ammunitionStockIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.ammunitionStockIdChangedByUser(value, unsetDependents);
    }
  }  
  
  ammunitionStockIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  costCentreDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }
  
  costCentreLoadSelectionList() {
    this.costCentreService.findAll().subscribe(
      data => {
        this.costCentreSelectionList = COST_CENTRE_TYPE.entityDecorator.ensureContained(this.item && this.item.costCentre, data.items);
      }
    );
  }

  costCentreChanging(value: CostCentre, unsetDependents: boolean = true) {}
  
  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.costCentreChangedByUser(value, unsetDependents);
    }
  }  
  
  costCentreChangedByUser(value: CostCentre, unsetDependents: boolean = true) {
  }
  
  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }
  
  calibreChanging(value: string, unsetDependents: boolean = true) {}
  
  calibreChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.calibreChangedByUser(value, unsetDependents);
    }
  }  
  
  calibreChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  closedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  closedChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.closedChangedByUser(value, unsetDependents);
    }
  }  
  
  closedChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  makeChanging(value: string, unsetDependents: boolean = true) {}
  
  makeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.makeChangedByUser(value, unsetDependents);
    }
  }  
  
  makeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  weightChanging(value: string, unsetDependents: boolean = true) {}
  
  weightChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.weightChangedByUser(value, unsetDependents);
    }
  }  
  
  weightChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get ammunitionStockId() {
    return this.control('ammunitionStockId');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get costCentre() {
    return this.control('costCentre');
  }
  
  get calibre() {
    return this.control('calibre');
  }
  
  get closed() {
    return this.control('closed');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get currentStockAmount() {
    return this.control('currentStockAmount');
  }
  
  get hasStocktake() {
    return this.control('hasStocktake');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get make() {
    return this.control('make');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
  get weight() {
    return this.control('weight');
  }
  
}





